/**
 * ArtistSlider - Horizontal slider for displaying artists on detail pages
 * 
 * Features:
 * - Groups artists by name to avoid duplicates
 * - Combines professions for same artist
 * - Sorts by max priority descending
 * - D-pad navigation support
 */
import React, { useCallback, useRef, useMemo, memo } from "react";
import {
    FocusContext,
    useFocusable,
} from "@noriginmedia/norigin-spatial-navigation";
import { useNavigate } from "react-router-dom";
import { getCMSImageURL } from "../../utils/cms.utils";
import { useInputMode } from "../../utils/hooks/useInputMode";

// ============================================================================
// ARTIST CARD COMPONENT
// ============================================================================

var ArtistItem = memo(function (props) {
    var artist = props.artist;
    var onFocus = props.onFocus;
    var focusKey = props.focusKey;

    var navigate = useNavigate();

    var handleNavigate = function () {
        if (artist.slug) {
            navigate("/artists/" + artist.slug);
        }
    };

    var focusableResult = useFocusable({
        focusKey: focusKey,
        onFocus: onFocus,
        onEnterPress: handleNavigate,
    });

    var focused = focusableResult.focused;
    var ref = focusableResult.ref;
    var focusSelf = focusableResult.focusSelf;

    var inputModeResult = useInputMode();
    var setMouseFocus = inputModeResult.setMouseFocus;
    var handleMouseEnter = useCallback(function () {
        setMouseFocus(true);
        focusSelf();
    }, [focusSelf, setMouseFocus]);

    // Memoize image URL to prevent recalculation on each render
    var imageUrl = useMemo(function () {
        return artist.image ? getCMSImageURL(artist.image, {
            format: 'jpeg',
            quality: 85,
            width: 250
        }) : null;
    }, [artist.image]);

    var name = artist.farsiName || artist.englishName || "";
    var initial = name.charAt(0).toUpperCase();

    // Memoize image style to prevent re-renders from regenerating the background-image
    var imageStyle = useMemo(function () {
        return imageUrl ? { backgroundImage: "url(" + imageUrl + ")" } : undefined;
    }, [imageUrl]);

    return (
        <div className="legacy-artist-slider-card" ref={ref} onClick={handleNavigate} onMouseEnter={handleMouseEnter}>
            <div className={"legacy-artist-slider-image-wrap" + (focused ? " is-focused" : "")}>
                {imageUrl ? (
                    <div className="legacy-artist-slider-image" style={imageStyle} />
                ) : (
                    <div className="legacy-artist-slider-placeholder">
                        <div className="legacy-artist-slider-placeholder-inner">{initial}</div>
                    </div>
                )}
            </div>
            <div className={"legacy-artist-slider-name" + (focused ? " is-focused" : "")}>{name}</div>
            {artist.profession && (
                <div className="legacy-artist-slider-profession">{artist.profession}</div>
            )}
        </div>
    );
});

// ============================================================================
// HELPER: Process artists with priority sorting
// ============================================================================

var getTextValue = function (value) {
    if (typeof value !== "string") return "";
    return value.trim();
};

var getProfessionName = function (profession) {
    if (!profession) return "";
    if (typeof profession === "string") return getTextValue(profession);
    return getTextValue(profession.name) ||
        getTextValue(profession.farsiName) ||
        getTextValue(profession.title);
};

var getPhotoSource = function (photo) {
    if (!photo) return "";
    if (typeof photo === "string") return photo;
    return photo.src || photo.image || photo.url || photo.path || photo.webp || "";
};

var getImageFromPhotos = function (contentPhotos) {
    if (!Array.isArray(contentPhotos) || contentPhotos.length === 0) return "";

    var preferredTypes = ["profile", "artist", "avatar", "image", "poster", "banner"];
    var photo = null;

    preferredTypes.some(function (type) {
        photo = contentPhotos.find(function (item) {
            return item && item.type === type;
        });
        return Boolean(photo);
    });

    return getPhotoSource(photo || contentPhotos[0]);
};

var normalizeArtistEntry = function (item) {
    if (!item || typeof item !== "object") return null;

    var artistData = item.artist || item;
    if (!artistData || typeof artistData !== "object") return null;

    var bin = artistData?.meta?.bin || artistData?.data?.meta?.bin || artistData;
    var id = artistData.id || bin.id || item.id;
    var slug = bin.slug || artistData.slug || item.slug;
    var farsiName = getTextValue(bin.farsiName) || getTextValue(artistData.farsiName) || getTextValue(item.farsiName);
    var englishName = getTextValue(bin.englishName) || getTextValue(artistData.englishName) || getTextValue(item.englishName);
    var name = farsiName || englishName || id;

    if (!name) return null;

    var image = bin.image ||
        artistData.image ||
        item.image ||
        getImageFromPhotos(bin.contentPhotos) ||
        getImageFromPhotos(artistData.contentPhotos) ||
        getImageFromPhotos(item.contentPhotos);

    return {
        id: id,
        slug: slug,
        farsiName: farsiName,
        englishName: englishName,
        name: name,
        image: image,
    };
};

/**
 * Groups artists by name, combines professions, and sorts by max priority
 * @param {Array} rawArtists - Raw artist data from API
 * @returns {Array} - Processed and sorted artist list
 */
var processArtists = function (rawArtists) {
    if (!rawArtists || !Array.isArray(rawArtists) || rawArtists.length === 0) {
        return [];
    }

    // Group by name (to avoid duplicates like same actor with different roles)
    var groupedArtists = {};

    rawArtists.forEach(function (item) {
        var normalized = normalizeArtistEntry(item);
        if (!normalized) return;

        if (!groupedArtists[normalized.name]) {
            groupedArtists[normalized.name] = {
                artist: normalized,
                entries: [],
                maxPriority: 0,
            };
        }

        groupedArtists[normalized.name].entries.push(item);

        // Track max priority
        var priority = item.priority || 0;
        if (priority > groupedArtists[normalized.name].maxPriority) {
            groupedArtists[normalized.name].maxPriority = priority;
        }
    });

    // Build final artist list
    var artists = [];

    Object.keys(groupedArtists).forEach(function (name) {
        var group = groupedArtists[name];
        if (group.entries.length === 0) return;

        // Combine all professions
        var professions = [];
        group.entries.forEach(function (entry) {
            var artistData = entry.artist || entry;
            var bin = artistData?.meta?.bin || artistData?.data?.meta?.bin || artistData;
            var profName = getProfessionName(entry.profession) ||
                getProfessionName(bin.profession) ||
                getProfessionName(artistData.profession);

            if (profName && professions.indexOf(profName) === -1) {
                professions.push(profName);
            }
        });

        artists.push({
            id: group.artist.id,
            slug: group.artist.slug,
            farsiName: group.artist.farsiName,
            englishName: group.artist.englishName,
            image: group.artist.image,
            profession: professions.join("، "),
            priority: group.maxPriority,
        });
    });

    // Sort by priority descending
    artists.sort(function (a, b) {
        return (b.priority || 0) - (a.priority || 0);
    });

    return artists;
};

function getMaxTranslateX(innerNode, scrollerRect, leftPadding, currentX) {
    var children = innerNode ? innerNode.children : null;
    var minLeft = Infinity;
    var i;

    if (!children || !children.length) return 0;

    for (i = 0; i < children.length; i += 1) {
        if (!children[i].getBoundingClientRect) continue;
        var rect = children[i].getBoundingClientRect();
        minLeft = Math.min(minLeft, rect.left - currentX);
    }

    if (minLeft === Infinity) return 0;
    return Math.max(0, scrollerRect.left + leftPadding - minLeft);
}

// ============================================================================
// MAIN SLIDER COMPONENT
// ============================================================================

var ArtistSlider = function (props) {
    var rawArtists = props.artists;
    var onFocus = props.onFocus;
    var title = props.title || "هنرمندان";

    var focusableResult = useFocusable({
        onFocus: onFocus,
        focusable: false,
        trackChildren: true,
        autoRestoreFocus: true,
        preferredChildFocusKey: "detail-artist-0",
        isFocusBoundary: true,
        focusBoundaryDirections: ["left"],
    });

    var ref = focusableResult.ref;
    var focusKey = focusableResult.focusKey;

    var innerRef = useRef(null);
    var scrollerRef = useRef(null);

    var inputModeResult = useInputMode();
    var isMouseFocusRef = inputModeResult.isMouseFocusRef;

    // Process artists with grouping and priority sorting
    var artists = useMemo(function () {
        return processArtists(rawArtists);
    }, [rawArtists]);

    var onAssetFocus = useCallback(function (focusProps) {
        if (onFocus) onFocus(focusProps);
        if (isMouseFocusRef.current) return; // Skip horizontal scroll on mouse hover
        if (!innerRef.current || !scrollerRef.current || !focusProps) return;

        var scrollerRect = scrollerRef.current.getBoundingClientRect();
        var leftPadding = 120;
        var rightPadding = 48;
        var currentTransform = 0;
        var transform = innerRef.current.style.transform || "";
        var transformMatch = /translateX\((-?[0-9.]+)px\)/.exec(transform);
        var nextTransform;
        var desiredForFocused = 0;
        var maxTransform;

        if (transformMatch) currentTransform = parseFloat(transformMatch[1]) || 0;

        nextTransform = currentTransform;
        if (focusProps.left < scrollerRect.left + leftPadding) {
            desiredForFocused = currentTransform + (scrollerRect.left + leftPadding - focusProps.left);
            nextTransform = desiredForFocused;
        } else if (focusProps.right > scrollerRect.right - rightPadding) {
            nextTransform = currentTransform - (focusProps.right - (scrollerRect.right - rightPadding));
        }

        maxTransform = Math.max(
            getMaxTranslateX(innerRef.current, scrollerRect, leftPadding, currentTransform),
            desiredForFocused
        );
        nextTransform = Math.max(0, Math.min(nextTransform, maxTransform));
        innerRef.current.style.transform = "translateX(" + Math.round(nextTransform * 100) / 100 + "px)";
    }, [isMouseFocusRef, onFocus]);

    // Don't render if no artists
    if (artists.length === 0) {
        return null;
    }

    return (
        <FocusContext.Provider value={focusKey}>
            <div className="legacy-artist-slider" ref={ref}>
                <div className="legacy-artist-slider-title">{title}</div>
                <div className="legacy-artist-slider-scroll" ref={scrollerRef}>
                    <div className="legacy-artist-slider-content" ref={innerRef}>
                        {artists.map(function (artist, index) {
                            return (
                                <ArtistItem
                                    key={artist.slug || artist.id || index}
                                    focusKey={"detail-artist-" + index}
                                    artist={artist}
                                    onFocus={onAssetFocus}
                                />
                            );
                        })}
                    </div>
                </div>
            </div>
        </FocusContext.Provider>
    );
};

export default ArtistSlider;
